<template>
  <section class="mine-varsler ods-padding-top-11">
    <h2
      class="ods-text--size-india ods-text--weight-medium ods-margin-top-0 ods-margin-bottom-5 ods-text--size-golf-breakpoint-medium"
    >
      {{ $t('avfallOgGjenvinning.paminnelser.title') }}
    </h2>
    <p class="p ods-text--size-kilo ods-text--size-juliett-breakpoint-medium">
      <template v-if="brukerinfo.email">
        {{ $t('avfallOgGjenvinning.paminnelser.epost', { epost: brukerinfo.email }) }}
        E-posten er hentet fra
        <a
          href="https://eid.difi.no/nb/kontakt-og-reservasjonsregisteret"
          target="_blank"
          rel="noopener noreferrer"
          title="Følg lenken for å lese mer om Kontakt- og reservasjonsregisteret"
          >Kontakt- og reservasjonsregisteret</a
        >.
      </template>
      <template v-else>
        {{
          $t('avfallOgGjenvinning.paminnelser.epost', {
            epost: $t('avfallOgGjenvinning.paminnelser.warning.idPortenEpostMissing'),
          })
        }}
      </template>
      {{ $t('avfallOgGjenvinning.paminnelser.avvik') }}
    </p>
    <div v-if="varslerFeiler" class="ok-alert ok-alert--danger" role="alert">
      {{ $t('avfallOgGjenvinning.paminnelser.error') }}
    </div>
    <div v-else class="ods-grid ods-grid--gap">
      <div class="ods-grid__column--12">
        <varsel-knapp
          v-for="(beholder, index) in unikeRenovasjonsbeholdere"
          :key="index"
          :varsel-aktivert="beholder.varselAktivert"
          :laster="varslerLaster"
          :beholdertype="beholder.beholdertype"
          @visModal="visModal(beholder.beholdertype)"
          @startVarsel="aktiverVarsel(beholder.beholdertype)"
        />
        <ok-modal v-show="erModalSynlig" @close="lukkModal()" @confirm="bekreftModalStoppVarsel()">
          <template #headerText>
            {{ $t('avfallOgGjenvinning.paminnelser.modal.title') }}
          </template>
          <template #body>
            {{ modalTekst }}
          </template>
        </ok-modal>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import OkModal from '@/components/OkModal.vue';
import VarselKnapp from '@/components/VarselKnapp.vue';

export default {
  name: 'MineVarslerTomminger',

  components: {
    OkModal,
    VarselKnapp,
  },

  props: {
    eiendom: {
      type: Object,
      required: true,
    },
  },

  computed: {
    ...mapState(['brukerinfo']),
    ...mapState('representasjoner', ['currentRepresentation']),
    ...mapState('eiendommer', ['eiendommer', 'eiendommerLoading', 'folkeregistrertAdresse']),
    ...mapState('avfallOgGjenvinning', ['varslerFeiler', 'varslerLaster', 'renovasjonsbeholdereLaster']),
    ...mapGetters('avfallOgGjenvinning', ['modalAvfallstype', 'erModalSynlig', 'unikeRenovasjonsbeholdere']),

    vegadresse() {
      const { vegadresse } = this.eiendom.postadresser[0];
      return `${vegadresse.gate} ${vegadresse.husnr || ''}${vegadresse.bokstav || ''}`;
    },
    modalTekst() {
      const avfallstypeStr = this.modalAvfallstype
        ? this.$t(`abonnementer.abonnementstyper.${this.modalAvfallstype}`).toLowerCase()
        : '';
      return this.$t('avfallOgGjenvinning.paminnelser.modal.tekst', {
        avfallstype: avfallstypeStr,
      });
    },
  },
  methods: {
    ...mapActions(['addGlobalMessage']),
    ...mapActions('avfallOgGjenvinning', ['startVarsel', 'stoppVarsel', 'visModal', 'lukkModal']),

    aktiverVarsel(beholdertype) {
      this.startVarsel({
        varselObjekt: this.lagVarselObjekt(beholdertype, this.eiendom, this.currentRepresentation.identifikator),
        beholdertype,
        txtInfo: this.$t('avfallOgGjenvinning.paminnelser.start.info', { type: beholdertype }),
        txtError: this.$t('avfallOgGjenvinning.paminnelser.start.error', { type: beholdertype }),
      });
    },

    deaktiverVarsel(beholdertype) {
      this.stoppVarsel({
        varselObjekt: this.lagVarselObjekt(beholdertype, this.eiendom, this.currentRepresentation.identifikator),
        beholdertype,
        txtInfo: this.$t('avfallOgGjenvinning.paminnelser.stop.info', { type: beholdertype }),
        txtError: this.$t('avfallOgGjenvinning.paminnelser.stop.error', { type: beholdertype }),
      });
    },

    hentEksternId(eiendom) {
      const { vegadresse } = eiendom.postadresser[0];
      const vegadr = `${vegadresse.gate.replace(/\s/g, '_')},${vegadresse.husnr || ''}${
        vegadresse.bokstav ? `,${vegadresse.bokstav}` : ''
      }`;
      if (eiendom.eierEiendom) {
        const gbs = `${eiendom.matrikkeladresse.gardsnr}.${eiendom.matrikkeladresse.bruksnr}${
          eiendom.matrikkeladresse.seksjonsnr ? `.${eiendom.matrikkeladresse.seksjonsnr}` : ''
        }`;
        return `${gbs}:${vegadr}`;
      }
      return `:${vegadr}`;
    },

    lagVarselUrl(eiendom) {
      const eksternIdWithColonUrlEncoded = this.hentEksternId(eiendom).replace(/:/, '%3A');
      return `${window.location.origin + window.location.pathname}?eiendom=${eksternIdWithColonUrlEncoded}`;
    },

    lagVarselObjekt(abonnementnavn, eiendom, fodselsnr) {
      const { vegadresse } = eiendom.postadresser[0];

      const adr = `${vegadresse.gate} ${vegadresse.husnr || ''}${vegadresse.bokstav || ''}`;

      const aboType = this.$t(`abonnementer.abonnementstyper.${abonnementnavn}`).toLowerCase();

      return {
        fodselsnr,
        abonnementstypeId: abonnementnavn,
        annenEpost: null,
        eksternId: this.hentEksternId(eiendom),
        ingress: this.$t('avfallOgGjenvinning.paminnelser.preface', { vegadresse: adr }),
        tittel: this.$t('avfallOgGjenvinning.paminnelser.abotitle', { type: aboType, vegadresse: adr }),
        url: this.lagVarselUrl(eiendom),
      };
    },

    bekreftModalStoppVarsel() {
      if (this.modalAvfallstype) {
        this.deaktiverVarsel(this.modalAvfallstype);
      } else {
        this.addGlobalMessage({
          messageType: 'error',
          message: this.$t('avfallOgGjenvinning.paminnelser.stop.error'),
        });
      }
    },
  },
};
</script>

<style lang="scss">
.p {
  hyphens: none;
}
</style>
